import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import { Calendar, Mail, AlertCircle } from 'lucide-react';
import { isGoogleAccountLinked } from '../../services/google/auth';
import { EmailList } from '../google/EmailList';
import { CalendarView } from '../google/CalendarView';
import { ProductivityStats } from '../google/ProductivityStats';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

export const ProductivityHub: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'email' | 'calendar'>('email');
  const [calendarView, setCalendarView] = useState<'day' | 'week'>('week');
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Vérifier si le compte Google est lié
  useEffect(() => {
    const checkGoogleAccount = async () => {
      try {
        setLoading(true);
        const linked = await isGoogleAccountLinked();
        setIsGoogleLinked(linked);
        if (!linked) {
          setError(t('productivityHub.errors.notConnected', 'Connectez votre compte Google depuis les paramètres administrateur pour accéder à cette fonctionnalité.'));
        }
      } catch (err) {
        console.error('Error checking Google account status:', err);
        setError(t('productivityHub.errors.checkFailed', 'Impossible de vérifier la connexion avec Google.'));
      } finally {
        setLoading(false);
      }
    };

    checkGoogleAccount();
  }, [t]);

  // Si le compte Google n'est pas lié, afficher un message d'erreur
  if (!loading && !isGoogleLinked) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('productivityHub.title', 'Google Hub')}</CardTitle>
            <CardDescription>
              {t('productivityHub.description', 'Gérer vos emails et votre calendrier Google')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('productivityHub.notConnected', 'Compte Google non connecté')}</AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
            <div className="mt-4">
              <Button 
                onClick={() => window.location.href = '/app/admin-settings'}
                variant="outline"
              >
                {t('productivityHub.goToSettings', 'Aller aux paramètres')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('productivityHub.title', 'Google Hub')}</h1>
          <p className="text-muted-foreground">
            {t('productivityHub.subtitle', 'Gérez vos emails et votre calendrier en un seul endroit')}
          </p>
        </div>
      </div>

      {/* Widgets statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ProductivityStats />
      </div>

      <Separator className="my-6" />

      {/* Contenu principal avec onglets */}
      <Tabs defaultValue="email" value={activeTab} onValueChange={(value) => setActiveTab(value as 'email' | 'calendar')} className="space-y-4">
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="email" className="flex items-center">
              <Mail className="mr-2 h-4 w-4" />
              {t('productivityHub.tabs.email', 'Emails')}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center">
              <Calendar className="mr-2 h-4 w-4" />
              {t('productivityHub.tabs.calendar', 'Calendrier')}
            </TabsTrigger>
          </TabsList>

          {/* Actions contextuelles en fonction de l'onglet actif */}
          {activeTab === 'calendar' && (
            <div className="flex items-center space-x-2">
              <TabsList>
                <TabsTrigger 
                  value="day" 
                  onClick={() => setCalendarView('day')}
                  data-active={calendarView === 'day'}
                  className={calendarView === 'day' ? 'bg-primary text-primary-foreground' : ''}
                >
                  {t('productivityHub.calendarView.day', 'Jour')}
                </TabsTrigger>
                <TabsTrigger 
                  value="week" 
                  onClick={() => setCalendarView('week')}
                  data-active={calendarView === 'week'}
                  className={calendarView === 'week' ? 'bg-primary text-primary-foreground' : ''}
                >
                  {t('productivityHub.calendarView.week', 'Semaine')}
                </TabsTrigger>
              </TabsList>
            </div>
          )}
        </div>

        <TabsContent value="email" className="space-y-4">
          <EmailList />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4">
          <CalendarView view={calendarView} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
